import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, RefreshCw, AlertCircle, AlertTriangle, Clock } from 'lucide-react';
import { axiosInstance } from '../App';

/**
 * AdminRemovalRequests — Iter 128 (Feb 2026) · Iter 133 widget (Feb 2026)
 *
 * Admin moderation tool for listing-removal / edit-suggestion requests
 * coming from /api/listings/{id}/removal-request. Approving a "removal"
 * request hides the listing from public surfaces and emails the requester.
 *
 * Iter 133 — top-of-page overdue warning widget (only renders when there
 * is at least one pending request older than 24h). Clicking it filters
 * the list to overdue pending requests.
 */
const STATUS_TABS = [
  { id: 'pending',  label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const OVERDUE_HOURS = 24;
const SUMMARY_TTL_MS = 30_000;  // dedupe back-to-back refresh / re-render calls

function ageHoursFrom(iso) {
  if (!iso) return 0;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.floor(ms / 3_600_000);
}

function humanAge(hours) {
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

const AdminRemovalRequests = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const [summary, setSummary] = useState(null);   // { overdueCount, oldestAgeHours, oldestCreatedAt }
  const summaryFetchedAt = useRef(0);
  const summaryInFlight = useRef(null);

  const fetchSummary = useCallback(async ({ force = false } = {}) => {
    // Dedupe: if we just fetched within the TTL, reuse the in-memory copy
    // unless the caller explicitly forces a refresh.
    const now = Date.now();
    if (!force && now - summaryFetchedAt.current < SUMMARY_TTL_MS) {
      return summary;
    }
    // Collapse concurrent calls onto a single in-flight promise.
    if (summaryInFlight.current) return summaryInFlight.current;
    summaryInFlight.current = (async () => {
      try {
        const r = await axiosInstance.get('/admin/removal-requests/overdue-summary');
        setSummary(r.data || null);
        summaryFetchedAt.current = Date.now();
        return r.data;
      } catch (e) {
        // Silent — widget should never break the page
        return null;
      } finally {
        summaryInFlight.current = null;
      }
    })();
    return summaryInFlight.current;
  }, [summary]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axiosInstance.get(`/admin/removal-requests?status=${status}`);
      setRows(r.data?.rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load removal requests');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const resolve = async (id, decision) => {
    const note = decision === 'rejected' ? window.prompt('Optional note to send to the requester (leave blank to skip):') : null;
    setResolvingId(id);
    try {
      await axiosInstance.post(`/admin/removal-requests/${id}/resolve`, {
        status: decision,
        adminNote: note || null,
      });
      await load();
      await fetchSummary({ force: true });  // overdue count may have just changed
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolvingId(null);
    }
  };

  // Filter rows client-side for the "overdue only" view. The backend already
  // returned ALL pending rows; we just hide non-overdue ones when the toggle
  // is on. Saves a second round-trip.
  const displayRows = useMemo(() => {
    if (!overdueOnly || status !== 'pending') return rows;
    return rows.filter((r) => ageHoursFrom(r.createdAt) >= OVERDUE_HOURS);
  }, [rows, overdueOnly, status]);

  const handleWidgetClick = () => {
    if (status !== 'pending') setStatus('pending');
    setOverdueOnly(true);
    // Scroll the list into view on mobile so the user sees the result
    setTimeout(() => {
      const list = document.querySelector('[data-testid="admin-removal-list"]')
                 || document.querySelector('[data-testid="admin-removal-empty"]');
      list?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const overdueCount = summary?.overdueCount || 0;
  const oldestAgeHours = summary?.oldestAgeHours || 0;
  // Severity: ≥3 OR oldest ≥72h → red; otherwise amber.
  const isRed = overdueCount >= 3 || oldestAgeHours >= 72;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100"
            data-testid="admin-removal-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold text-slate-900">Removal Requests</h1>
          <button
            onClick={() => { load(); fetchSummary({ force: true }); }}
            className="ml-auto p-1.5 rounded-lg text-slate-600 hover:bg-slate-100"
            data-testid="admin-removal-refresh"
            aria-label="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Iter 133 — overdue warning widget. Only renders when there's at
            least one pending request older than 24h. */}
        {overdueCount > 0 && (
          <button
            type="button"
            onClick={handleWidgetClick}
            className={`w-full text-left rounded-xl border-2 px-4 py-3 sm:py-3.5 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 ${
              isRed
                ? 'bg-red-50 border-red-300 hover:border-red-400 focus:ring-red-300'
                : 'bg-amber-50 border-amber-300 hover:border-amber-400 focus:ring-amber-300'
            }`}
            data-testid="admin-removal-overdue-widget"
            aria-label={`${overdueCount} overdue removal request${overdueCount === 1 ? '' : 's'} — click to filter`}
          >
            <div className="flex items-start sm:items-center gap-3 flex-wrap sm:flex-nowrap">
              <span className={`flex-shrink-0 p-2 rounded-full ${isRed ? 'bg-red-100' : 'bg-amber-100'}`}>
                <AlertTriangle className={`h-5 w-5 ${isRed ? 'text-red-700' : 'text-amber-700'}`} aria-hidden="true" />
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm sm:text-base font-semibold ${isRed ? 'text-red-900' : 'text-amber-900'}`}>
                  {overdueCount} pending request{overdueCount === 1 ? '' : 's'} overdue
                  <span className="text-xs font-normal opacity-70 ml-1">({'>'}24h)</span>
                </p>
                <p className={`text-xs sm:text-sm mt-0.5 ${isRed ? 'text-red-800' : 'text-amber-800'}`}>
                  <Clock className="inline h-3.5 w-3.5 -mt-0.5 mr-1" aria-hidden="true" />
                  Oldest: <strong>{humanAge(oldestAgeHours)}</strong> · click to review
                </p>
              </div>
              <span
                className={`flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${
                  isRed ? 'bg-red-600 text-white' : 'bg-amber-600 text-white'
                }`}
                data-testid="admin-removal-overdue-count-badge"
              >
                {overdueCount}
              </span>
            </div>
          </button>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setStatus(tab.id); setOverdueOnly(false); }}
                className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                  status === tab.id
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`admin-removal-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {overdueOnly && status === 'pending' && (
            <button
              type="button"
              onClick={() => setOverdueOnly(false)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-800 border border-red-200 hover:bg-red-200"
              data-testid="admin-removal-overdue-clear"
              title="Show all pending requests"
            >
              Overdue ({'>'}24h) only · clear filter <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Body */}
        {loading ? (
          <p className="text-sm text-slate-500" data-testid="admin-removal-loading">Loading…</p>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-sm text-red-800" data-testid="admin-removal-error">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        ) : displayRows.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="admin-removal-empty">
            {overdueOnly && status === 'pending'
              ? 'No overdue pending requests right now — nicely done.'
              : `No ${status} requests.`}
          </p>
        ) : (
          <ul className="space-y-2" data-testid="admin-removal-list">
            {displayRows.map((r) => {
              const isOverdue = r.status === 'pending' && ageHoursFrom(r.createdAt) >= OVERDUE_HOURS;
              return (
                <li
                  key={r.id}
                  className={`bg-white rounded-xl p-4 space-y-2 border ${isOverdue ? 'border-red-300 ring-1 ring-red-200' : 'border-slate-200'}`}
                  data-testid={`admin-removal-row-${r.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900">{r.businessName}</p>
                        {isOverdue && (
                          <span
                            className="text-[10px] font-bold uppercase bg-red-600 text-white px-1.5 py-0.5 rounded"
                            data-testid={`admin-removal-overdue-tag-${r.id}`}
                          >
                            Overdue
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {r.type === 'removal' ? 'Remove listing' : 'Suggest edit'} · {r.ownershipStatus || 'unknown'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {r.requesterName} &lt;{r.requesterEmail}&gt; · {r.requesterRole}
                      </p>
                    </div>
                    <span className="text-[11px] text-slate-400 whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <blockquote className="text-sm text-slate-700 bg-slate-50 border-l-4 border-slate-300 px-3 py-2 rounded">
                    {r.reason}
                  </blockquote>
                  {r.status === 'pending' ? (
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => resolve(r.id, 'approved')}
                        disabled={resolvingId === r.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold disabled:opacity-60"
                        data-testid={`admin-removal-approve-${r.id}`}
                      >
                        <Check className="h-3.5 w-3.5" />Approve
                      </button>
                      <button
                        onClick={() => resolve(r.id, 'rejected')}
                        disabled={resolvingId === r.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 text-xs font-semibold disabled:opacity-60"
                        data-testid={`admin-removal-reject-${r.id}`}
                      >
                        <X className="h-3.5 w-3.5" />Reject
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">
                      {r.status === 'approved' ? '✓ Approved' : '✗ Rejected'} by {r.resolvedBy} on {new Date(r.resolvedAt).toLocaleString()}
                      {r.adminNote ? ` — “${r.adminNote}”` : ''}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminRemovalRequests;
